import React, { useState } from 'react'
import { apiPost } from '../../utils/api'
import { ZohoStats } from './ZohoChatsView'

interface VectorSearchTabProps {
  stats: ZohoStats | null
  onImport: () => void
  isImporting: boolean
}

interface SearchResult {
  chat_number: number
  visitor_name: string | null
  score: number
  snippet: string
  chat_date: string | null
}

export const VectorSearchTab: React.FC<VectorSearchTabProps> = ({ stats, onImport, isImporting }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [isChunking, setIsChunking] = useState(false)
  const [chunkResult, setChunkResult] = useState<string | null>(null)
  // chunkStats will be populated when Qdrant is connected

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) return

    setSearching(true)
    setHasSearched(true)
    try {
      const data = await apiPost<{ results: SearchResult[] }>('/vector/search', {
        query: searchQuery.trim(),
        limit: 20,
      })
      setResults(data.results || [])
    } catch (err) {
      console.error('Vector search failed:', err)
      setResults([])
    } finally {
      setSearching(false)
    }
  }

  const handleChunk = async () => {
    setIsChunking(true)
    setChunkResult(null)
    try {
      const result = await apiPost<{ message: string; chunked: number; skipped: number; errors: number }>(
        '/zoho-chats/chunk'
      )
      setChunkResult(
        `Chunked ${result.chunked} chats (${result.skipped} already done, ${result.errors} errors)`
      )
    } catch (err) {
      setChunkResult(`Chunking failed: ${(err as Error).message}`)
    } finally {
      setIsChunking(false)
    }
  }

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      {/* Stats cards */}
      <div className="px-6 py-5">
        <div className="grid grid-cols-4 gap-4">
          <StatCard
            label="Total Documents"
            value={stats?.total_chats?.toLocaleString() || '0'}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            }
            color="blue"
          />
          <StatCard
            label="Embedded Chats"
            value={stats?.embedded_chats?.toLocaleString() || '0'}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
              </svg>
            }
            color="green"
          />
          <StatCard
            label="Embedding Model"
            value="MiniLM-L6-v2"
            subtitle="384-dim vectors"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
            }
            color="purple"
          />
          <StatCard
            label="Vector Store"
            value="Qdrant"
            subtitle="Not connected"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
              </svg>
            }
            color="amber"
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-6 pb-4">
        <div className="flex items-center gap-3 p-4 bg-surface-secondary rounded-xl border border-border">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-txt-primary dark:text-white">Pipeline Actions</h3>
            <p className="text-xs text-txt-secondary mt-0.5">
              Import PDFs from the Zoho folder, chunk text for processing, then embed into Qdrant
            </p>
          </div>
          <button
            onClick={onImport}
            disabled={isImporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isImporting ? (
              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            )}
            {isImporting ? 'Importing...' : '1. Import PDFs'}
          </button>
          <button
            onClick={handleChunk}
            disabled={isChunking}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {isChunking ? (
              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
              </svg>
            )}
            {isChunking ? 'Chunking...' : '2. Chunk Text'}
          </button>
          <button
            disabled
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-amber-600 text-white opacity-50 cursor-not-allowed"
            title="Qdrant not connected yet"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
            3. Embed Vectors
          </button>
        </div>
        {chunkResult && (
          <p className="text-xs text-txt-secondary mt-2 px-1">{chunkResult}</p>
        )}
      </div>

      {/* Semantic search */}
      <div className="px-6 pb-4">
        <div className="bg-surface-secondary rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-txt-primary dark:text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
              Semantic Search
            </h3>
            <p className="text-xs text-txt-secondary mt-0.5">
              Search across all chat transcripts using natural language (requires Qdrant connection)
            </p>
          </div>
          <div className="p-4">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-secondary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input
                  type="text"
                  placeholder="e.g. customer asking about quote pricing for blinds..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-border bg-surface-primary text-txt-primary placeholder-txt-secondary focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600"
                />
              </div>
              <button
                type="submit"
                disabled={searching || !searchQuery.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {searching ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                )}
                Search
              </button>
            </form>

            {/* Results */}
            {hasSearched && (
              <div className="mt-4">
                {results.length === 0 ? (
                  <div className="text-center py-8 text-txt-secondary">
                    <svg className="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    <p className="text-sm font-medium">No results found</p>
                    <p className="text-xs mt-1">
                      Vector search requires Qdrant to be running and chats to be embedded
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {results.map((r, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg border border-border bg-surface-primary hover:bg-surface-secondary transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-txt-primary dark:text-white">
                              {r.visitor_name || 'Unknown'}
                            </span>
                            <span className="text-[10px] font-mono text-txt-secondary bg-surface-tertiary px-1.5 py-0.5 rounded">
                              #{r.chat_number}
                            </span>
                          </div>
                          <span className="text-[10px] font-mono text-green-500 bg-green-500/10 px-2 py-0.5 rounded">
                            {(r.score * 100).toFixed(1)}% match
                          </span>
                        </div>
                        <p className="text-xs text-txt-secondary line-clamp-3 leading-relaxed">
                          {r.snippet}
                        </p>
                        {r.chat_date && (
                          <span className="text-[10px] text-txt-secondary mt-1 inline-block">
                            {new Date(r.chat_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Department breakdown */}
      {stats && stats.departments && Object.keys(stats.departments).length > 0 && (
        <div className="px-6 pb-6">
          <div className="bg-surface-secondary rounded-xl border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-txt-primary dark:text-white">Chat Distribution</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                {/* By department */}
                <div>
                  <p className="text-xs font-medium text-txt-secondary mb-2 uppercase tracking-wider">By Department</p>
                  <div className="space-y-1.5">
                    {Object.entries(stats.departments)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 8)
                      .map(([dept, count]) => (
                        <div key={dept} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-txt-primary dark:text-white truncate">{dept}</span>
                              <span className="text-txt-secondary ml-2">{count}</span>
                            </div>
                            <div className="mt-0.5 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                              <div
                                className="h-full rounded-full bg-brand-600"
                                style={{ width: `${(count / stats.total_chats) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                {/* By country */}
                <div>
                  <p className="text-xs font-medium text-txt-secondary mb-2 uppercase tracking-wider">By Country</p>
                  <div className="space-y-1.5">
                    {Object.entries(stats.countries || {})
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 8)
                      .map(([country, count]) => (
                        <div key={country} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-txt-primary dark:text-white truncate">{country}</span>
                              <span className="text-txt-secondary ml-2">{count}</span>
                            </div>
                            <div className="mt-0.5 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                              <div
                                className="h-full rounded-full bg-green-500"
                                style={{ width: `${(count / stats.total_chats) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Stat Card ──────────────────────────────────────────────────── */

const colorMap = {
  blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  green: 'bg-green-500/10 text-green-600 dark:text-green-400',
  purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
}

const StatCard: React.FC<{
  label: string
  value: string
  subtitle?: string
  icon: React.ReactNode
  color: keyof typeof colorMap
}> = ({ label, value, subtitle, icon, color }) => (
  <div className="bg-surface-secondary rounded-xl border border-border p-4">
    <div className="flex items-center gap-3">
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${colorMap[color]}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-txt-secondary">{label}</p>
        <p className="text-lg font-bold text-txt-primary dark:text-white leading-tight">{value}</p>
        {subtitle && <p className="text-[10px] text-txt-secondary">{subtitle}</p>}
      </div>
    </div>
  </div>
)
